import imgCompression from 'browser-image-compression';
import sha1 from 'sha1';

import { EXCEL_APPLICATION_EXTENSION, JPG_IMAGE_EXTENSION, MAX_FILE_SIZE_MB, PNG_IMAGE_EXTENSION } from '../utils/constants';
import { PDFImageProps } from '../utils/models';
import { updateLastPhotoNumber } from '../store/reportDetails';

import config from '../utils/config';
import http from './httpService';
import logger from './logService';
import pdfService from './pdfService';
import toastService from './toastService';

import * as utils from '../utils/utilities';

const cloudName = process.env.REACT_APP_CLOUD_NAME as string;
const apiKey = process.env.REACT_APP_CLOUDINARY_API_KEY as string;
const apiUrl = process.env.REACT_APP_CLOUDINARY_API as string;

const endpoint: string = apiUrl + `/${cloudName}/auto/upload`;

interface CloudinaryResponse {
	format: string;
	original_filename: string;
	secure_url: string;
}

const generateRequestSignature = (publicID: string, params?: { [key: string]: string }): { signature: string; timestamp: number } => {
	const timestamp = Math.round(new Date().getTime() / 1000);
	const apiSecret = process.env.REACT_APP_CLOUDINARY_API_SECRET as string;

	const parameters = params
		? Object.keys(params)
				.map((key) => `${key}=${params[key]}`)
				.join('&')
		: '';
	const stringToSign = `public_id=${publicID}${
		parameters.length > 0 ? '&' : ''
	}${parameters}&timestamp=${timestamp}${apiSecret}`;

	return { signature: sha1(stringToSign), timestamp };
};

const transformPDFFileToImage = async (file: File) => {
	const formData = new FormData();

	const publicID = utils.formatUniquePublicID(file.name);
	const result = generateRequestSignature(publicID);

	formData.append('file', file);
	formData.append('timestamp', result.timestamp.toString());
	formData.append('signature', result.signature);
	formData.append('api_key', apiKey);
	formData.append('public_id', publicID);


	try {
		const { data } = await http.post<CloudinaryResponse>(endpoint, formData, utils.removeAccessToken());
		const imageUrls = await pdfService.getPDFImageUrl(data.secure_url);

		const mappedUrls: PDFImageProps[] = imageUrls.map((url) => ({
			refNo: updateLastPhotoNumber(),
			src: url,
			caption: Date.now() + data.original_filename,
			format: JPG_IMAGE_EXTENSION,
		}));

		return mappedUrls;
	} catch (error) {
		logger.log(error as Error);
		return [];
	}
};

const uploadImageFiles = async (files: File[]) => {
	let urls: PDFImageProps[] = [];

	for (const file of files) {
		const formData = new FormData();
		const publicID = utils.formatUniquePublicID(file.name);

		if (utils.getIsExcelFile(file)) {
			if (config.enableExcelToPngConversion) {
				const { signature, timestamp } = generateRequestSignature(publicID, { raw_convert: 'aspose' });

				formData.append('file', file);
				formData.append('raw_convert', 'aspose');
				formData.append('timestamp', timestamp.toString());
				formData.append('signature', signature);
			} else {
				const { signature, timestamp } = generateRequestSignature(publicID);

				formData.append('file', file);
				formData.append('timestamp', timestamp.toString());
				formData.append('signature', signature);
			}
		} else {
			const { signature, timestamp } = generateRequestSignature(publicID);

			if (config.enablePDFToPngConversion && utils.getIsPDFFile(file)) {
				formData.append('file', file);
			} else {
				const compressedFile = await imgCompression(file, { maxSizeMB: MAX_FILE_SIZE_MB });

				formData.append('file', compressedFile);
			}

			formData.append('timestamp', timestamp.toString());
			formData.append('signature', signature);
		}

		formData.append('public_id', publicID);
		formData.append('api_key', apiKey);

		try {
			const { data } = await http.post<CloudinaryResponse>(
				endpoint,
				formData,
				utils.removeAccessToken()
			);


			if (config.enablePDFToPngConversion && utils.getIsPDFFile(file)) {
				const imageUrls = await pdfService.getPDFImageUrl(data.secure_url);

				const mappedUrls: PDFImageProps[] = imageUrls.map((url) => ({
					refNo: updateLastPhotoNumber(),
					src: url,
					caption: data.original_filename,
					format: JPG_IMAGE_EXTENSION,
				}));

				urls = urls.concat(mappedUrls);
			} else if (utils.getIsExcelFile(file)) {
				const nextPhotoNumber = updateLastPhotoNumber();

				if (config.enableExcelToPngConversion) {
					urls.push({
						refNo: nextPhotoNumber,
						src: utils.replaceKeywordInReference('raw', 'image', data.secure_url) + '.' + PNG_IMAGE_EXTENSION,
						caption: file.name,
						format: PNG_IMAGE_EXTENSION,
					});
				} else {
					urls.push({
						refNo: nextPhotoNumber,
						src: data.secure_url,
						caption: file.name,
						format: EXCEL_APPLICATION_EXTENSION,
					});
				}
			} else {
				urls.push({
					refNo: updateLastPhotoNumber(),
					src: utils.getOptimizedImageURL(data.secure_url),
					caption: file.name,
					format: data.format,
				});
			}
		} catch (error) {
			logger.log(error as Error);
		}
	}

	return urls;
};

const uploadImageString = async (fileName: string, imageString: string) => {
	const updatedFileName = utils.formatUniquePublicID(fileName);
	const requestDetails = generateRequestSignature(updatedFileName);
	const formData = new FormData();

	formData.append('public_id', updatedFileName);
	formData.append('file', imageString);
	formData.append('timestamp', requestDetails.timestamp.toString());
	formData.append('signature', requestDetails.signature);
	formData.append('api_key', apiKey);

	try {
		const result = await http.post<CloudinaryResponse>(
			endpoint,
			formData,
			utils.removeAccessToken()
		);

		return result.data.secure_url;
	} catch (error) {
		console.log('cloudinary upload error:', error);
		
		const errorMessage: string = utils.getErrorMessage(error);
		toastService.error(errorMessage);

		return null;
	}
};

const getSingleImageURL = (publicID: string) => {
	const extension = publicID.substring(publicID.length - 3).toLowerCase();
	const url = `${process.env.REACT_APP_CLOUDINARY_ASSET_URL}/${cloudName}/image/upload/${publicID}.${extension}`;

	return url;
};

export { uploadImageFiles, uploadImageString, getSingleImageURL, transformPDFFileToImage };
